import { useEffect, useRef, useState } from "react";
import { api, getUser, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pin, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function extractMentions(text, users) {
  const ids = new Set();
  const re = /@([\wäöüß]+(?:\.[\wäöüß]+)?)/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const q = m[1].toLowerCase();
    const hit = users.find((u) => (u.email || "").toLowerCase().startsWith(q) || (u.name || "").toLowerCase().includes(q));
    if (hit) ids.add(hit.id);
  }
  return [...ids];
}

export default function Comments({ orderId, taskId }) {
  const me = getUser();
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [mentionQuery, setMentionQuery] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const textareaRef = useRef(null);

  const params = orderId ? { related_order_id: orderId } : { related_task_id: taskId };

  const load = async () => {
    const [c, u] = await Promise.all([api.get("/comments", { params }), api.get("/team/users-lite")]);
    setList(c.data); setUsers(u.data);
  };
  useEffect(() => { load();  }, [orderId, taskId]);

  const submit = async () => {
    if (!text.trim()) return;
    const mentions = extractMentions(text, users);
    try {
      await api.post("/comments", { text, mentioned_user_ids: mentions }, { params });
      setText(""); setMentionQuery(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const onChange = (v) => {
    setText(v);
    const idx = textareaRef.current?.selectionStart ?? v.length;
    const before = v.slice(0, idx);
    const m = /@([\wäöüß.]*)$/i.exec(before);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const insertMention = (u) => {
    const idx = textareaRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, idx).replace(/@([\wäöüß.]*)$/i, `@${(u.email || u.name).split(" ")[0]} `);
    const after = text.slice(idx);
    setText(before + after);
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  const filteredUsers = mentionQuery !== null
    ? users.filter((u) => (u.email || "").toLowerCase().includes(mentionQuery) || (u.name || "").toLowerCase().includes(mentionQuery)).slice(0, 8)
    : [];

  const del = async (c) => { if (!window.confirm("Kommentar löschen?")) return; await api.delete(`/comments/${c.id}`); load(); };
  const pin = async (c) => { await api.post(`/comments/${c.id}/pin`); load(); };
  const startEdit = (c) => { setEditingId(c.id); setEditText(c.text); };
  const saveEdit = async (c) => {
    try {
      await api.patch(`/comments/${c.id}`, { text: editText, mentioned_user_ids: extractMentions(editText, users) });
      setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  return (
    <div className="space-y-4" data-testid="comments-section">
      <div className="space-y-3">
        {list.length === 0 && <div className="text-sm text-slate-500 italic">Noch keine Kommentare.</div>}
        {list.map((c) => (
          <div key={c.id} className={`flex gap-3 p-3 rounded-md border ${c.pinned ? "bg-amber-50/40 border-amber-200" : "bg-white border-slate-200"}`} data-testid={`comment-${c.id}`}>
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">{initials(c.author_name)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <b className="text-slate-800">{c.author_name}</b>
                <span>· {fmtDateTime(c.created_at)}</span>
                {c.edited_at && <span className="italic">(bearbeitet)</span>}
                {c.pinned && <span className="inline-flex items-center gap-1 text-amber-700"><Pin className="w-3 h-3" /> angepinnt</span>}
              </div>
              {editingId === c.id ? (
                <div className="mt-2 space-y-2">
                  <Textarea rows={2} value={editText} onChange={(e) => setEditText(e.target.value)} />
                  <div className="flex gap-2"><Button size="sm" onClick={() => saveEdit(c)}>Speichern</Button><Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Abbrechen</Button></div>
                </div>
              ) : (
                <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1" data-testid={`comment-text-${c.id}`}>{c.text}</div>
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {(me.rolle === "admin" || me.rolle === "manager") && <Button size="sm" variant="ghost" onClick={() => pin(c)} data-testid={`comment-pin-${c.id}`}><Pin className="w-3.5 h-3.5" /></Button>}
              {c.author_user_id === me.id && <Button size="sm" variant="ghost" onClick={() => startEdit(c)}><Edit3 className="w-3.5 h-3.5" /></Button>}
              {(c.author_user_id === me.id || me.rolle === "admin") && <Button size="sm" variant="ghost" onClick={() => del(c)}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <div className="relative">
        <Textarea ref={textareaRef} rows={2} value={text} onChange={(e) => onChange(e.target.value)}
          placeholder="Kommentar schreiben… (@ für Mention)" data-testid="comment-input" />
        {mentionQuery !== null && filteredUsers.length > 0 && (
          <div className="absolute z-10 mt-1 bg-white border border-slate-200 rounded-md shadow-md w-64 max-h-48 overflow-auto" data-testid="mention-popover">
            {filteredUsers.map((u) => (
              <button key={u.id} type="button" onClick={() => insertMention(u)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </button>
            ))}
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <Button onClick={submit} disabled={!text.trim()} data-testid="comment-submit">Kommentar senden</Button>
        </div>
      </div>
    </div>
  );
}
